import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import type { Partner } from "@/app/api/types/partner";
import type { ChatSession } from "@stockline/shared/types/ai/chat-session";
import type { ChatMessage } from "@stockline/shared/types/ai/chat-message";
import type { ChatExecution } from "@stockline/shared/types/ai/chat-execution";
import type { ChatMedia } from "@stockline/shared/types/ai/chat-media";

// Database configuration
export const DB_NAME = "stockline-offline-db";
export const DB_VERSION = 6;

// Store names
export const STORES = {
  PRE_ORDERS: "preOrders",
  SYNC_QUEUE: "syncQueue",
  METADATA: "metadata",
  PARTNERS: "partners",
  CHAT_SESSIONS: "chatSessions",
  CHAT_MESSAGES: "chatMessages",
  CHAT_EXECUTIONS: "chatExecutions",
  CHAT_MEDIA: "chatMedia",
} as const;

// PreOrder cache entry
export interface CachedPreOrder {
  key: string; // `${date}:${preOrderId}`
  date: string; // YYYY-MM-DD for indexing
  preOrderId: string;
  preOrder: PreOrder; // Full object with flows
  lastSyncedAt: number; // Timestamp when synced
  serverUpdatedAt: string; // Server's updated_at
  accessedAt: number; // For LRU eviction
  createdAt: number; // When cached
  expiresAt: number; // createdAt + 30 days
}

// Cached partner entry for offline support
export interface CachedPartner {
  id: string; // partner_id (temp ID like "temp-partner-xxx" or real UUID)
  partner: Partner; // Full partner object
  isOffline: boolean; // true if created offline, not yet synced
  createdAt: number; // When cached
  syncedAt?: number; // When synced to server (undefined if not synced)
}

// Cached chat entries for offline support
export interface CachedChatSession {
  id: string;
  data: ChatSession;
  syncId: number;
  lastSyncedAt: number;
}

export interface CachedChatMessage {
  id: string;
  data: ChatMessage;
  syncId: number;
  sessionId: string; // Index for session filtering
  lastSyncedAt: number;
}

export interface CachedChatExecution {
  id: string;
  data: ChatExecution;
  syncId: number;
  sessionId: string;
  lastSyncedAt: number;
}

export interface CachedChatMedia {
  id: string;
  data: ChatMedia;
  syncId: number;
  sessionId: string;
  messageId?: string;
  lastSyncedAt: number;
}

// Sync change entry - simplified entity-level tracking
export interface SyncChange {
  id: string; // UUID for queue tracking
  entityType:
    | "pre-order"
    | "flow"
    | "partner"
    | "chat-session"
    | "chat-message"
    | "chat-media";
  entityId: string; // UUID of the entity
  operation: "create" | "update" | "delete";
  data: Record<string, any>; // Entity data for create/update, empty for delete
  updatedAt: string; // Client-generated ISO timestamp
  status: "pending" | "syncing" | "failed";
  retryCount: number;
  lastRetryAt?: number;
  createdAt: number; // When this change was queued
}

// Database metadata
export interface OfflineDBMetadata {
  key: string;
  value: unknown;
  updatedAt: number;
}
